import { ReactElement, useMemo } from 'react'
import { useController } from 'react-hook-form'
import { find } from 'lodash-es'
import { Autocomplete, MenuItem, TextField } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { getSteelCSShapes } from '@queries'
import { compactFieldSx } from '../../../../../../../../components/CrossSectionInput/utils'

interface Props {
  disabled?: boolean
  crossSectionFieldPath: string
  'data-cy'?: string
}

const SteelShapeCompactSelect = ({
  disabled,
  crossSectionFieldPath,
  'data-cy': dataCy = 'crosssection-steel-shape-select',
}: Props): ReactElement => {
  const shapeFieldPath = `${crossSectionFieldPath}.shape`
  const { field } = useController({ name: shapeFieldPath })
  const { data, isLoading } = useQuery(getSteelCSShapes.key, getSteelCSShapes.request)

  const options = useMemo(
    () =>
      (data ?? []).map((s: SteelCSShape) => ({
        value: s,
        label: s.profile,
      })),
    [data],
  )

  const selectedOption = useMemo(() => {
    const fv = field.value as SteelCSShape | undefined
    if (!fv?.profile) return null
    return find(options, o => o.value.profile === fv.profile) ?? null
  }, [field.value, options])

  return (
    <Autocomplete
      size="small"
      disableClearable
      loading={isLoading}
      disabled={disabled}
      options={options}
      getOptionLabel={o => o.label}
      isOptionEqualToValue={(a, b) => a.value.profile === b.value.profile}
      value={selectedOption}
      onChange={(_, newOpt) => {
        if (newOpt) field.onChange(newOpt.value)
      }}
      onBlur={field.onBlur}
      data-cy={dataCy}
      renderOption={(props, option, { index }) => (
        <MenuItem {...props} key={option.value.profile} data-cy={`${dataCy}-item-${index}`}>
          {option.label}
        </MenuItem>
      )}
      renderInput={params => (
        <TextField
          {...params}
          disabled={disabled}
          inputRef={field.ref}
          fullWidth
          sx={compactFieldSx}
        />
      )}
    />
  )
}

export default SteelShapeCompactSelect
